import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  ExternalLink,
  FileText,
  Github,
  Loader2,
  Send,
  Star,
  Upload,
  Users,
} from 'lucide-react';
import projectWorkService from '../services/projectWorkService';

const TYPE_LABELS = {
  it: { label: 'Progetto IT', icon: Github, accept: null },
  pdf: { label: 'PDF', icon: FileText, accept: '.pdf' },
  ppt: { label: 'PowerPoint', icon: FileText, accept: '.ppt,.pptx' },
  doc: { label: 'Documento', icon: FileText, accept: '.doc,.docx' },
  image: { label: 'Immagine', icon: FileText, accept: '.jpg,.jpeg,.png,.gif,.svg,.webp' },
  free: { label: 'Libero', icon: Upload, accept: '*' },
};

const STATUS_LABELS = {
  pending_confirmation: 'In attesa di conferme',
  submitted: 'Consegnato',
  evaluating: 'In valutazione',
  evaluated: 'Valutato',
};

const DATE_TIME_FORMATTER = new Intl.DateTimeFormat('it-IT', {
  timeZone: 'Europe/Rome',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

function formatDateTime(value) {
  if (!value) {
    return '-';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return DATE_TIME_FORMATTER.format(date);
}

export default function ProjectWork({
  courseId,
  service = projectWorkService,
  activityId = null,
  initialProjectWork = null,
  title = 'Project Work',
  emptyTitle = 'Nessuna attivita pratica attiva',
  emptyDescription = 'Il docente non ha ancora attivato questa sezione per questo corso.',
  loadingLabel = 'Caricamento attivita pratica...',
  groupMissingLabel = 'Il docente non ti ha ancora assegnato a un gruppo per questa attivita.',
  sectionKey = 'project-work',
  hideWhenInactive = false,
}) {
  const [loading, setLoading] = useState(true);
  const [pw, setPw] = useState(null);
  const [group, setGroup] = useState(null);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [submission, setSubmission] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [repoUrl, setRepoUrl] = useState('');
  const [githubUsername, setGithubUsername] = useState('');
  const [showTrace, setShowTrace] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (initialProjectWork) {
      setPw(initialProjectWork);
      setGroup(initialProjectWork.group || null);
      setRepoUrl(initialProjectWork?.group?.github_repo_url || '');
      setGithubUsername(initialProjectWork?.current_user_github_username || '');
      const milestoneSubmission =
        initialProjectWork?.milestones?.find((milestone) => milestone.submission)?.submission || null;
      setSubmission(milestoneSubmission || initialProjectWork?.latest_submission || initialProjectWork?.submission || null);
      setLoading(false);
    }

    loadProjectWork(true);
  }, [courseId, activityId]); // eslint-disable-line react-hooks/exhaustive-deps

  const typeInfo = TYPE_LABELS[pw?.delivery_type || pw?.type] || TYPE_LABELS.free;
  const displayTitle = pw?.name?.trim() ? pw.name.trim() : `${title} - ${typeInfo.label}`;

  const activeMilestone = useMemo(() => {
    if (!pw?.milestones?.length) {
      return null;
    }

    const firstOpenMilestone = pw.milestones.find((milestone) => {
      const milestoneSubmission = milestone.submission;
      return !milestoneSubmission || milestoneSubmission.status === 'pending_confirmation';
    });

    return firstOpenMilestone || pw.milestones[0];
  }, [pw]);

  const activeSubmission = activeMilestone?.submission || submission || null;
  const isGroupMode = pw?.mode === 'group';
  const traceText = group?.trace || null;
  const traceConfirmed = Boolean(group?.trace_confirmed);
  const isItDelivery = (pw?.delivery_type || pw?.type) === 'it';
  const needsGithubUsername = isItDelivery && !githubUsername.trim();
  const canUpload = Boolean(group) && traceConfirmed && Boolean(activeMilestone) && !activeSubmission;
  const canConfirm = Boolean(activeSubmission?.can_confirm);
  const capacityLabel = pw?.max_group_size > 0 ? String(pw.max_group_size) : 'illimitato';
  const confirmedIds = activeSubmission?.confirmed_by || [];
  const confirmationProgress = `${activeSubmission?.confirmed_count ?? 0} / ${activeSubmission?.required_confirmations ?? group?.members?.length ?? 0}`;
  const activityContextLabel = title === 'Prove Intermedie' ? 'Prova Intermedia' : 'Project Work';
  const assignmentLabelCapitalized = isGroupMode ? 'Gruppo' : 'Assegnazione individuale';
  const traceLabel = isGroupMode ? 'la traccia del gruppo' : 'la tua traccia';
  const submissionLabel = isGroupMode ? 'del gruppo' : 'individuale';
  const participantFallbackLabel = isGroupMode ? 'uno studente del gruppo' : 'te';
  const deliveredItemFallbackLabel = isGroupMode ? 'consegna del gruppo' : 'consegna individuale';
  const missingAssignmentLabel = isGroupMode
    ? groupMissingLabel
    : 'Il docente non ha ancora reso disponibile la tua assegnazione individuale per questa attivita.';

  const confirmationMembers = (group?.members || []).map((member) => ({
    ...member,
    hasConfirmed: confirmedIds.includes(member.user_id),
  }));

  useEffect(() => {
    if (activeSubmission?.status !== 'evaluating') {
      return undefined;
    }

    const intervalId = setInterval(() => {
      loadProjectWork(false);
    }, 15000);

    return () => clearInterval(intervalId);
  }, [activeSubmission?.status, courseId, activityId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadProjectWork(showLoader = false) {
    if (showLoader) {
      setLoading(true);
    }

    try {
      const data = await service.getProjectWork(courseId, activityId);
      const nextProjectWork = data.project_work || null;

      setPw(nextProjectWork);
      setGroup(nextProjectWork?.group || null);
      setAvailableGroups([]);
      setRepoUrl(nextProjectWork?.group?.github_repo_url || '');
      setGithubUsername(nextProjectWork?.current_user_github_username || '');

      const milestoneSubmission =
        nextProjectWork?.milestones?.find((milestone) => milestone.submission)?.submission || null;

      setSubmission(milestoneSubmission || nextProjectWork?.latest_submission || nextProjectWork?.submission || null);

      if (nextProjectWork && nextProjectWork.mode === 'group' && !nextProjectWork.group) {
        const groupsResponse = await service.listGroups(courseId, activityId);
        setAvailableGroups(groupsResponse.groups || []);
      }

      setError('');
    } catch {
      setError(`Impossibile caricare ${title.toLowerCase()}.`);
      setAvailableGroups([]);
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
  }

  async function handleCreateRepo() {
    if (!group) {
      return;
    }

    setBusy(true);
    setError('');
    setSuccess('');

    try {
      await service.createGithubRepo(group.id);
      await loadProjectWork(false);
      setSuccess('Repo GitHub creata con successo.');
    } catch (err) {
      setError(err.response?.data?.message || 'Errore durante la creazione della repo GitHub.');
    } finally {
      setBusy(false);
    }
  }

  async function handleSaveGithubUsername() {
    if (!githubUsername.trim()) {
      setError('Inserisci prima il tuo username GitHub personale.');
      return;
    }

    setBusy(true);
    setError('');
    setSuccess('');

    try {
      const response = await service.saveGithubUsername(githubUsername.trim());
      setGithubUsername(response.github_username || githubUsername.trim());
      await loadProjectWork(false);
      setSuccess('Username GitHub salvato correttamente.');
    } catch (err) {
      setError(err.response?.data?.message || 'Errore durante il salvataggio dello username GitHub.');
    } finally {
      setBusy(false);
    }
  }

  async function handleSubmit() {
    if (!group || !activeMilestone) {
      return;
    }

    setBusy(true);
    setError('');
    setSuccess('');

    try {
      const response = await service.submit(
        group.id,
        !isItDelivery ? selectedFile : null,
        activeMilestone.id,
        isItDelivery ? repoUrl.trim() : null
      );

      setSelectedFile(null);
      await loadProjectWork(false);

      if (response.all_confirmed) {
        setSuccess(isItDelivery ? (isGroupMode ? 'Repository confermata dal gruppo.' : 'Repository confermata.') : (isGroupMode ? 'Consegna confermata dal gruppo.' : 'Consegna confermata.'));
      } else {
        setSuccess(
          isItDelivery
            ? (isGroupMode ? 'Repository inviata. In attesa delle conferme degli altri membri.' : 'Repository inviata.')
            : (isGroupMode ? 'File caricato. In attesa delle conferme degli altri membri.' : 'File caricato.')
        );
      }
    } catch (err) {
      setError(err.response?.data?.message || (isItDelivery
        ? 'Errore durante l\'invio della repository.'
        : 'Errore durante il caricamento del file.'));
    } finally {
      setBusy(false);
    }
  }

  async function handleConfirmSubmission() {
    if (!activeSubmission?.id) {
      return;
    }

    setBusy(true);
    setError('');
    setSuccess('');

    try {
      const response = await service.confirmSubmission(activeSubmission.id);
      await loadProjectWork(false);

      if (response.all_confirmed) {
        setSuccess(isGroupMode ? 'Tutti i membri del gruppo hanno confermato il file.' : 'Conferma registrata.');
      } else {
        setSuccess('Conferma registrata. In attesa delle altre conferme.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Errore durante la conferma del file.');
    } finally {
      setBusy(false);
    }
  }

  async function handleJoinGroup(groupId) {
    setBusy(true);
    setError('');
    setSuccess('');

    try {
      await service.joinGroup(groupId);
      await loadProjectWork(false);
      setSuccess('Ingresso nel gruppo completato.');
    } catch (err) {
      setError(err.response?.data?.message || 'Errore durante l\'ingresso nel gruppo.');
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-8 shadow-lg flex items-center justify-center gap-3 text-gray-500">
        <Loader2 className="w-6 h-6 animate-spin" />
        {loadingLabel}
      </div>
    );
  }

  if (!pw) {
    if (hideWhenInactive) {
      return null;
    }

    return (
      <div className="bg-white rounded-xl p-8 shadow-lg text-center">
        <Github className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-gray-700">{emptyTitle}</h3>
        <p className="text-sm text-gray-400 mt-1">
          {emptyDescription}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      {success && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex gap-2">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
          {success}
        </div>
      )}

      <div className="bg-white rounded-xl p-5 shadow-lg">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <typeInfo.icon className="w-5 h-5 text-blue-600" />
              {displayTitle}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Modalita: {pw.mode === 'group' ? `Gruppo (max ${capacityLabel} persone)` : 'Individuale'}
              {activeMilestone?.deadline &&
                ` - Scadenza attiva: ${formatDateTime(activeMilestone.deadline)}`}
            </p>
          </div>
        </div>

        {traceText && (
          <div className="mt-4 border border-gray-100 rounded-lg overflow-hidden">
            <button
              onClick={() => setShowTrace((prev) => !prev)}
              className="w-full flex items-center justify-between px-4 py-3 bg-blue-50 text-blue-800 font-medium text-sm hover:bg-blue-100 transition"
            >
              <span>{`Visualizza ${traceLabel}`}</span>
                {showTrace ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            {showTrace && (
              <div className="p-4 text-sm text-gray-700 whitespace-pre-wrap">
                {traceText}
              </div>
            )}
          </div>
        )}
      </div>

      {pw.milestones?.length > 0 && (
        <div className="bg-white rounded-xl p-5 shadow-lg">
          <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-600" />
            Milestone
          </h3>
          <div className="space-y-3">
            {pw.milestones.map((milestone) => {
              const milestoneSubmission = milestone.submission;

              return (
                <div key={milestone.id} className="rounded-lg border border-gray-200 p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-semibold text-gray-800">{milestone.title}</p>
                      <p className="text-sm text-gray-500">
                        Tipo: {milestone.type === 'final' ? 'Consegna finale' : 'Revisione'}
                      </p>
                      <p className="text-sm text-gray-500">
                        Scadenza:{' '}
                        {milestone.deadline
                          ? formatDateTime(milestone.deadline)
                          : 'Nessuna scadenza'}
                      </p>
                    </div>
                    <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 text-gray-700">
                      {milestoneSubmission ? STATUS_LABELS[milestoneSubmission.status] || milestoneSubmission.status : 'Non inviata'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!group && isGroupMode && (
        <div className="bg-white rounded-xl p-5 shadow-lg border-l-4 border-yellow-400">
          <div className="flex items-center gap-3">
            <Users className="w-5 h-5 text-yellow-600" />
            <div>
              <p className="font-semibold text-gray-800">{`${assignmentLabelCapitalized} non ancora disponibile`}</p>
              <p className="text-sm text-gray-500">
                {missingAssignmentLabel}
              </p>
            </div>
          </div>
        </div>
      )}

      {!group && pw.mode === 'group' && (
        <div className="bg-white rounded-xl p-5 shadow-lg" data-section={sectionKey}>
          <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            Gruppi disponibili
          </h3>

          {availableGroups.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-200 p-4 text-sm text-gray-500">
              Nessun gruppo disponibile al momento.
            </div>
          ) : (
            <div className="space-y-3">
              {availableGroups.map((availableGroup) => {
                const memberCount = availableGroup.members?.length || 0;
                const isFull = pw.max_group_size > 0 && memberCount >= pw.max_group_size;

                return (
                  <div key={availableGroup.id} className="rounded-lg border border-gray-200 p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="font-semibold text-gray-800">{availableGroup.name}</p>
                        <p className="text-sm text-gray-500">
                          Partecipanti: {memberCount} / {capacityLabel}
                        </p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {(availableGroup.members || []).map((member) => (
                            <span
                              key={member.user_id}
                              className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-700"
                            >
                              {member.name}
                              {member.is_leader ? ' (leader)' : ''}
                            </span>
                          ))}
                        </div>
                      </div>
                      <button
                        onClick={() => handleJoinGroup(availableGroup.id)}
                        disabled={busy || isFull}
                        className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
                        {isFull ? 'Gruppo pieno' : 'Entra nel gruppo'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {group && (
        <div className="bg-white rounded-xl p-5 shadow-lg">
          <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            {`${assignmentLabelCapitalized} ${activityContextLabel}: ${group.name}`}
          </h3>

          <div className="mb-3">
            <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
              Sezione attiva: {title}
            </span>
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            {group.members.map((member) => (
              <div
                key={member.user_id}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${
                  member.is_me
                    ? 'bg-blue-50 border-blue-200 text-blue-700'
                    : 'bg-green-50 border-green-200 text-green-700'
                }`}
              >
                <CheckCircle2 className="w-3 h-3" />
                {member.name}
                {member.is_me ? ' (tu)' : ''}
                {member.is_leader ? ' *' : ''}
              </div>
            ))}
          </div>

          {!traceConfirmed && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
              {`La traccia ${isGroupMode ? 'del gruppo' : 'individuale'} non e ancora stata approvata dal docente.`}
            </div>
          )}

          {isItDelivery && traceConfirmed && (
            <div className="mb-4">
              {needsGithubUsername && (
                <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <p className="text-sm font-medium text-amber-900">
                    {`Prima di usare la repo ${isGroupMode ? 'del gruppo' : 'individuale'} devi salvare il tuo username GitHub personale.`}
                  </p>
                  <p className="mt-1 text-xs text-amber-800">
                    Serve per associare i commit al tuo contributo nella valutazione AI.
                  </p>
                  <div className="mt-3 flex flex-col gap-3 sm:flex-row">
                    <input
                      type="text"
                      value={githubUsername}
                      onChange={(event) => setGithubUsername(event.target.value)}
                      placeholder="es. tuo-username-github"
                      className="w-full rounded-lg border border-amber-300 px-4 py-2 text-sm text-gray-800 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    />
                    <button
                      onClick={handleSaveGithubUsername}
                      disabled={busy || !githubUsername.trim()}
                      className="inline-flex items-center justify-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-amber-700 disabled:opacity-50"
                    >
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Github className="w-4 h-4" />}
                      Salva username GitHub
                    </button>
                  </div>
                </div>
              )}

              <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                {`La consegna di questo elaborato avviene tramite repository GitHub ${isGroupMode ? 'del gruppo' : 'individuale'}. Incolla il link della repo oppure creala automaticamente, poi inviala${isGroupMode ? ' per la conferma del gruppo' : ''}.`}
              </div>
              <div className="space-y-3">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Link repository GitHub
                  </label>
                  <input
                    type="url"
                    value={repoUrl}
                    onChange={(event) => setRepoUrl(event.target.value)}
                    placeholder="https://github.com/organizzazione/repository"
                    className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-800 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </div>

                <div className="flex flex-wrap gap-2">
                  {group.github_repo_url && (
                    <a
                      href={group.github_repo_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition"
                    >
                      <Github className="w-4 h-4" />
                      Apri repo GitHub
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}

                  <button
                    onClick={handleCreateRepo}
                    disabled={busy || needsGithubUsername}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-50 transition"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Github className="w-4 h-4" />}
                    Crea repo GitHub
                  </button>

                  {canUpload && (
                    <button
                      onClick={handleSubmit}
                      disabled={busy || !repoUrl.trim() || needsGithubUsername}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold text-sm disabled:opacity-50 transition"
                    >
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      Invia repository{activeMilestone ? ` - ${activeMilestone.title}` : ''}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 border-t border-gray-100 pt-5">
            <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
              <Upload className="w-5 h-5 text-blue-600" />
              {`Consegna ${submissionLabel}`}
            </h4>

            {!traceConfirmed && (
              <div className="mb-4 rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800">
                La consegna si sblocca dopo l'approvazione della traccia da parte del docente.
              </div>
            )}

            {activeSubmission?.status === 'pending_confirmation' && (
              <div className="mb-4 rounded-xl border border-yellow-200 bg-yellow-50 p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm text-yellow-900">
                      {isItDelivery ? 'Repository inviata da ' : 'File caricato da '}
                      <strong>{activeSubmission.submitted_by_name || participantFallbackLabel}</strong>:{' '}
                      <strong>{activeSubmission.original_filename || activeSubmission.github_repo_url || deliveredItemFallbackLabel}</strong>
                    </p>
                    <p className="mt-2 text-sm font-medium text-yellow-900">
                      {`${isGroupMode ? 'Conferme gruppo' : 'Stato conferme'}: ${confirmationProgress}`}
                    </p>
                  </div>
                  <span className="rounded-full bg-yellow-100 px-3 py-1 text-xs font-semibold text-yellow-900">
                    In attesa
                  </span>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {confirmationMembers.map((member) => (
                    <div
                      key={member.user_id}
                      className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${
                        member.hasConfirmed ? 'bg-green-100 text-green-800' : 'bg-white text-gray-600 border border-yellow-200'
                      }`}
                    >
                      <CheckCircle2 className="w-3 h-3" />
                      {member.name}
                      {member.is_me ? ' (tu)' : ''}
                      {member.hasConfirmed ? ' - confermato' : ' - in attesa'}
                    </div>
                  ))}
                </div>

                {canConfirm ? (
                  <button
                    onClick={handleConfirmSubmission}
                    disabled={busy}
                    className="mt-4 inline-flex items-center gap-2 rounded-lg bg-yellow-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-yellow-700 disabled:opacity-50"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    Conferma file
                  </button>
                ) : (
                  <p className="mt-4 text-xs text-yellow-800">
                    Hai gia confermato oppure sei l'autore del caricamento.
                  </p>
                )}
              </div>
            )}

            {(activeSubmission?.status === 'submitted' || activeSubmission?.status === 'evaluating' || activeSubmission?.status === 'evaluated') && (
              <div className="mb-4 rounded-xl border border-green-200 bg-green-50 p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm text-green-900">
                      {isItDelivery ? 'Ultima repository confermata: ' : 'Ultimo file confermato: '}
                      <strong>{activeSubmission.original_filename || activeSubmission.github_repo_url || deliveredItemFallbackLabel}</strong>
                    </p>
                    <p className="mt-1 text-sm text-green-800">
                      {`Caricato da ${activeSubmission.submitted_by_name || participantFallbackLabel}`}
                    </p>
                  </div>
                  <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-800">
                    {STATUS_LABELS[activeSubmission.status] || activeSubmission.status}
                  </span>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  {activeSubmission.file_url && (
                    <a
                      href={activeSubmission.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-medium text-gray-800 border border-green-200 transition hover:bg-green-100"
                    >
                      <FileText className="w-4 h-4" />
                      Visualizza / scarica file
                    </a>
                  )}
                  {activeSubmission.github_repo_url && (
                    <a
                      href={activeSubmission.github_repo_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-medium text-gray-800 border border-green-200 transition hover:bg-green-100"
                    >
                      <Github className="w-4 h-4" />
                      Apri repo consegnata
                    </a>
                  )}
                </div>
                {activeSubmission.submitted_at && (
                  <p className="mt-3 text-sm text-green-800">
                    Data invio: {formatDateTime(activeSubmission.submitted_at)}
                  </p>
                )}
              </div>
            )}

            {canUpload && !isItDelivery && (
              <div className="mb-4">
                <input
                  ref={fileRef}
                  type="file"
                  accept={typeInfo.accept || '*'}
                  onChange={(event) => setSelectedFile(event.target.files[0] || null)}
                  className="hidden"
                />
                <button
                  onClick={() => fileRef.current?.click()}
                  className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 hover:border-blue-400 rounded-lg text-sm text-gray-600 hover:text-blue-600 transition w-full justify-center"
                >
                  <Upload className="w-4 h-4" />
                  {selectedFile ? selectedFile.name : `Carica file ${typeInfo.label}`}
                </button>
              </div>
            )}

            {canUpload && !isItDelivery && (
              <button
                onClick={handleSubmit}
                disabled={busy || !selectedFile}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold text-sm disabled:opacity-50 transition"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Carica file{activeMilestone ? ` - ${activeMilestone.title}` : ''}
              </button>
            )}
          </div>

          {activeSubmission?.status === 'evaluated' && (
            <div className="mb-4 bg-white rounded-xl border-l-4 border-green-500 p-5 shadow-sm">
              <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-500" />
                Risultato valutazione AI
              </h3>
              <div className="flex items-center gap-4 mb-4">
                <div className="text-5xl font-black text-blue-600">
                  {activeSubmission.ai_grade}
                  <span className="text-2xl text-gray-400">/10</span>
                </div>
                <div className="text-sm text-gray-500">
                  Valutato il{' '}
                  {formatDateTime(activeSubmission.evaluated_at)}
                </div>
              </div>
              {activeSubmission.ai_feedback_student && (
                <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {activeSubmission.ai_feedback_student}
                </div>
              )}
            </div>
          )}

          {activeSubmission?.status === 'evaluating' && (
            <div className="mb-4 bg-white rounded-xl border-l-4 border-blue-400 p-5 shadow-sm">
              <div className="flex items-start gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-gray-800">Valutazione AI in corso...</p>
                    <Loader2 className="w-5 h-5 text-blue-500 animate-spin" aria-hidden="true" />
                  </div>
                  <p className="text-sm text-gray-500">Polling attivo: la valutazione comparira automaticamente appena pronta.</p>
                </div>
              </div>
            </div>
          )}

          {activeSubmission?.status === 'submitted' && (
            <div className="mb-4 bg-white rounded-xl border-l-4 border-emerald-500 p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                <div>
                  <p className="font-semibold text-gray-800">{`Consegna confermata ${submissionLabel}`}</p>
                  <p className="text-sm text-gray-500">
                    {activeSubmission.ai_feedback_student
                      ? 'La valutazione AI non è stata completata automaticamente.'
                      : 'Il file e ora visibile al docente.'}
                  </p>
                </div>
              </div>
              {activeSubmission.ai_feedback_student && (
                <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 whitespace-pre-wrap">
                  {activeSubmission.ai_feedback_student}
                </div>
              )}
            </div>
          )}

        </div>
      )}
    </div>
  );
}
